import { duration } from '@play-co/components';
import { State } from '../State';
import {
  ChatbotMessageInstance,
  deliverChatbotMessage,
  getJitterDelayInMs,
} from '../chatbot/chatbot.util';
import {
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import { getLocaleConfig } from '../creatives/text';
import { isDynamicTestInBucket } from '../getters/ab';
import { getLocale } from '../getters/user';
import {
  getDailyBonusRemainingCooldown,
  isMatch2DailyBonusActive,
} from '../match2/match2.getters';
import ab from '../ruleset/ab';
import { ScheduledActionAPI, SyncActionAPI } from '../utils/types';
import { isGuestUser } from './guest.getters';
import { getJSTDateTime } from '../utils/time';
import * as match2TournamentGetters from '../match2/tournament/match2-tournament.getters';
import { magicLampsIsAvailable } from '../match2/magiclamps/magiclamps.getters';
import { isTreasureExpressAvailable } from '../treasureExpress/treasureExpress.getters';
import { getEquippedPetSpecies } from '../pet/pet.getters';
import { dreamDriftIsAvailable } from '../dreamDrift/dreamDrift.getters';
import { isWeeklyLeaderboardsAvailable } from '../weeklyLeaderboard/weeklyLeaderboard.getters';

/**
 * Configuration for guest notifications.
 */
type GuestNotificationConfig = {
  /** Feature name for the notification */
  subFeature: string;
  /** The hour of the notification 0-23 */
  hour: number;
  /** List of weekdays when the notification should be sent */
  weekdays: WeekdayShort[];
  /** Function to determine if the notification can be delivered */
  canDeliver: (state: State, now: number) => boolean;
};

/**
 * Map of guest notifications to their respective feature and subFeature.
 */
export const guestNotificationMap = {
  // NEW MESSAGES
  dailyBonus: {
    subFeature: 'notification_dailyBonusNew',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    hour: 10,
    canDeliver: (state: State, now: number) => {
      if (!isMatch2DailyBonusActive(state)) return false;
      return getDailyBonusRemainingCooldown(state, now) === 0;
    },
  },
  tournamentLive: {
    subFeature: 'notification_tournamentLive',
    weekdays: ['Mon', 'Wed', 'Fri'],
    hour: 8,
    canDeliver: match2TournamentGetters.canPlay,
  },
  tournamentOngoing: {
    subFeature: 'notification_tournamentOngoing',
    weekdays: ['Sun', 'Tue', 'Thu'],
    hour: 15,
    canDeliver: match2TournamentGetters.canPlay,
  },
  magicLampsStart: {
    subFeature: 'notification_lampsStartNew',
    weekdays: ['Mon'],
    hour: 13,
    canDeliver: magicLampsIsAvailable,
  },
  magicLampsEnd: {
    subFeature: 'notification_lampsEndNew',
    weekdays: ['Tue'],
    hour: 22,
    canDeliver: magicLampsIsAvailable,
  },
  treasureExpressLive: {
    subFeature: 'notification_treasureExpressLive',
    weekdays: ['Fri'],
    hour: 13,
    canDeliver: isTreasureExpressAvailable,
  },
  treasureExpressOngoing: {
    subFeature: 'notification_treasureExpressOngoing',
    weekdays: ['Sat'],
    hour: 13,
    canDeliver: isTreasureExpressAvailable,
  },
  buruLunch: {
    subFeature: 'notification_buruAttentionLunch',
    weekdays: ['Wed'],
    hour: 13,
    canDeliver: (state: State) => !!getEquippedPetSpecies(state),
  },
  buruEvening: {
    subFeature: 'notification_buruAttention_evening',
    weekdays: ['Mon', 'Wed', 'Thu'],
    hour: 22,
    canDeliver: (state: State) => !!getEquippedPetSpecies(state),
  },
  // EXISTING ADAPTED MESSAGES
  dreamDrift: {
    subFeature: 'notification_dreamDrift',
    weekdays: ['Fri'],
    hour: 22,
    canDeliver: dreamDriftIsAvailable,
  },
  weeklyLeaderboards28h: {
    subFeature: 'notification_weekly_leaderboards_28h_new',
    weekdays: ['Sat'],
    hour: 19,
    canDeliver: isWeeklyLeaderboardsAvailable,
  },
  weeklyLeaderboards4h: {
    subFeature: 'notification_weekly_leaderboards_4h_new',
    weekdays: ['Sun'],
    hour: 19,
    canDeliver: isWeeklyLeaderboardsAvailable,
  },
} as const satisfies Record<string, GuestNotificationConfig>;

/**
 * List of ids for guest notifications.
 */
export const guestNotificationIds = Object.keys(
  guestNotificationMap,
) as (keyof typeof guestNotificationMap)[];

/**
 * Type representing the guest notifications.
 */
export type GuestNotificationId = keyof typeof guestNotificationMap;

/**
 * Checks if the given notification ID is a valid guest notification ID.
 */
export function isValidGuestNotificationId(id: string) {
  return guestNotificationIds.includes(id as GuestNotificationId);
}

/**
 * Checks if guest notifications are enabled for the user.
 * @param state
 * @returns
 */
export function areGuestNotificationsEnabled(state: State) {
  return isGuestUser(state);
}

/**
 * Sends a push notification to the guest user. It will automatically schedule the next notification.
 * @param state The user state.
 * @param api The API for sending notifications.
 * @param args The arguments containing the receiver ID and notification type.
 */
export function sendGuestPN(
  state: State,
  api: SyncActionAPI | ScheduledActionAPI,
  args: {
    itemId: GuestNotificationId;
  },
) {
  // Only deliver if guest notifications are enabled
  if (!areGuestNotificationsEnabled(state)) return;

  // Double-check the id of the notification.
  if (!isValidGuestNotificationId(args.itemId)) {
    console.error(`Invalid guest notification ID: ${args.itemId}`);
    return;
  }

  // Schedule the next notification already
  scheduleNextGuestPN(state, api, args.itemId);

  // Check if the notification can be delivered
  const config = guestNotificationMap[args.itemId];
  if (!config.canDeliver(state, api.date.now())) {
    return;
  }

  // Define the payload for the chatbot message
  const payload = generateReplicantChatbotPayload({
    feature: 'guestNotification',
    $subFeature: config.subFeature,
  });

  // Create the chatbot message using the invite template
  const message = chatbotMessageTemplates.simplePushNotification({
    args: getLocaleConfig(getLocale(state)).guest[args.itemId],
    payload,
  });

  // Deliver the chatbot message
  deliverChatbotMessage(state, api, {
    receiverId: state.id,
    message,
    channel: 'push',
  });
}

/**
 * Schedule all guest notifications, if guest notifications are enabled.
 */
export function scheduleGuestNotifications(
  state: State,
  api: SyncActionAPI | ScheduledActionAPI,
) {
  // Only schedule if guest notifications are enabled
  if (!areGuestNotificationsEnabled(state)) return;

  // Schedule all guest notifications
  for (const itemId of guestNotificationIds) {
    scheduleNextGuestPN(state, api, itemId);
  }
}

/**
 * Schedules the next guest push notification by id.
 */
function scheduleNextGuestPN(
  state: State,
  api: SyncActionAPI | ScheduledActionAPI,
  itemId: GuestNotificationId,
) {
  // Only schedule if guest notifications are enabled
  if (!areGuestNotificationsEnabled(state)) return;

  const config = guestNotificationMap[itemId];
  const delayToNextMessage = getDelayToNextWeekdayTime({
    now: api.date.now(),
    hour: config.hour,
    weekdays: config.weekdays,
  });

  // Jitter delay to avoid all notifications being sent exactly at the same time
  const delayJitter = getJitterDelayInMs(
    api.math.random,
    duration({ minutes: 20 }),
  );

  // Final delay for the message, in milliseconds
  let delayInMS = delayToNextMessage + delayJitter;

  // Remove the jitter on development, to make it easier to test
  if (process.env.IS_DEVELOPMENT) {
    delayInMS = delayToNextMessage;
  }

  // Schedule the notification
  api.scheduledActions.schedule.sendGuestMessage({
    args: { notificationId: itemId },
    notificationId: `guestNotification_${itemId}`,
    delayInMS,
  });
}

/**
 * List of weekdays in short format.
 * Starts from Monday because of Luxon's DateTime weekday numbering:
 * Monday = 1, Tuesday = 2, ..., Sunday = 7.
 */
const weekdayShorts = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

/**
 * Type representing a short weekday.
 */
type WeekdayShort = (typeof weekdayShorts)[number];

/**
 * Returns the delay in milliseconds until the next occurrence of the specified weekday and hour.
 * Examples :
 * If today is Sunday and the target is [Mon, Tue, Wed] 10 AM, it will return the delay until Monday 10 AM.
 * If today is Monday 9 AM and the target is [Mon, Tue, Wed] 10 AM, it will return the delay until Monday 10 AM (today).
 * If today is Monday 11 AM and the target is [Mon, Tue, Wed] 10 AM, it will return the delay until Tuesday 10 AM (next target day).
 * If today is Monday and the target is [Wed, Thu, Fri] 10 AM, it will return the delay until Wednesday 10 AM.
 */
export function getDelayToNextWeekdayTime(args: {
  now: number;
  hour: number;
  weekdays: WeekdayShort[];
}) {
  const now = getJSTDateTime(args.now);
  const currentWeekdayIndex = now.weekday - 1; // Convert to 0-based index
  const targetIndices = args.weekdays.map((day) => weekdayShorts.indexOf(day));

  // Check if today is a target day and if the target time hasn't passed yet
  const targetTimeToday = now.set({
    hour: args.hour,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  const isTodayTargetDay = targetIndices.includes(currentWeekdayIndex);
  const hasTargetTimePassed = now.toMillis() > targetTimeToday.toMillis(); // Changed >= to >

  // If today is a target day and the time hasn't passed, schedule for today
  if (isTodayTargetDay && !hasTargetTimePassed) {
    return targetTimeToday.toMillis() - now.toMillis();
  }

  // Find the next target day (excluding today if it's a target day and time has passed)
  const daysToAdd = targetIndices
    .map((targetIndex) => {
      // Calculate days until this target day
      let daysUntil = (targetIndex - currentWeekdayIndex + 7) % 7;
      // If this is today (daysUntil === 0), skip to next week for this target
      if (daysUntil === 0) {
        daysUntil = 7;
      }
      return daysUntil;
    })
    .reduce((min, days) => Math.min(min, days), 7);

  // Calculate the exact time for the notification
  return targetTimeToday.plus({ days: daysToAdd }).toMillis() - now.toMillis();
}

/**
 * List of notification subFeatures that should be blocked for guests, if the guest notifications are enabled.
 */
export const guestNotificationBlacklist: string[] = [
  'evening',
  'lunch',
  'morning',

  'notification_fuji',
  'notification_matchLife',
  'notification_waterballoon',
  'notification_moveLikeBuruburu',
  'notification_lollipopWednesday',
  'notification_streakChampion',
  'notification_yellowFestival',
  'notification_miniFirecracker',
  'notification_tournamentStart',
  'notification_tournamentTomorrow',
  'notification_tournamentSoon',
  'notification_tournamentEnd',
  'notification_lampsStart',
  'notification_lampsEnd',
  'notification_turtleRace',
  'notification_weekly_leaderboards_28h',
  'notification_weekly_leaderboards_4h',

  'status_very_hungry',
  'status_scared',
  'status_hungry',
  'status_evolving',
  'status_dirty',
  'status_very_dirty',
  'status_very_lonely',
  'status_lonely',
  'status_pee',
  'status_tooth_decay',
  'status_very_pee',

  'visit_non_friend',
  'visit_friend',

  'duo_invite',
  'duo_response',
  'duo_reminder',
] as const;

/**
 * Check if a message is blacklisted for guests.
 */
export function isMessageBlacklistedForGuests(
  state: State,
  message: ChatbotMessageInstance,
): boolean {
  // No blacklist if guest notifications are not enabled
  if (!areGuestNotificationsEnabled(state)) {
    return false;
  }

  // Retrieve the subFeature from the message payload
  const subFeature = message.payload.$subFeature ?? '';

  // Check if the subFeature is in the blacklist
  return guestNotificationBlacklist.includes(subFeature);
}
